/**
 * NewWorkerPage.js — 3-step wizard to onboard a new worker
 *
 * Step 0 — Personal Details: name, phone, date of joining, date of birth, Aadhar number
 * Step 1 — Photos: avatar (front camera / gallery), Aadhar front, Aadhar back
 * Step 2 — Employment: team, shift, rate, leave policy
 *
 * Manager-only: non-managers are redirected to / immediately.
 *
 * Camera: "Camera" button opens a getUserMedia viewfinder with a contextual guide
 * overlay (oval for avatar, card-ratio frame for Aadhar). Always uses the rear
 * (environment) camera — a supervisor takes the photo, not the worker. Works on
 * both mobile and desktop. "Gallery" button opens a plain file picker.
 *
 * File objects are tracked separately from preview URLs. Uploads run in parallel
 * before the worker record is created.
 *
 * Directus file folders:
 *   photo       → 03692f53-c7b8-4870-b89b-66603258715e
 *   aadhar_*    → 8e0f7795-9354-4361-9498-2cdb4dc90f7b
 *
 * Fields NOT set by this form (managed by backend):
 *   history, total_earnings, total_payouts, total_deductions, current_balance,
 *   doe, exit_reason, user_created, date_created, user_updated, date_updated
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { showAlert } from '../components/UI';
import { playSuccessSound, playErrorSound } from '../utils/audio';
import {
  getShifts, getRates, getLeavePolicies, getTeams,
  createWorker, uploadFile,
} from '../api/directus';
import { useAuth } from '../context/AuthContext';
import { toISODate } from '../utils/helpers';

const PHOTO_FOLDER  = '03692f53-c7b8-4870-b89b-66603258715e';
const AADHAR_FOLDER = '8e0f7795-9354-4361-9498-2cdb4dc90f7b';

const STEP_LABELS = ['Personal', 'Photos', 'Employment'];

const todays = () => toISODate(new Date());

/** Format YYYY-MM-DD → DD-MM-YYYY for display. */
const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

/** Display Aadhar as groups of 4: 1234 5678 9012. */
const groupAadhar = (digits) => digits.replace(/(.{4})/g, '$1 ').trim();

const BLANK = {
  fullName: '', phone: '', doj: todays(), dob: '', aadhar: '',
  avatar: null, aadharFront: null, aadharBack: null, // blob preview URLs
  shift: '', rate: '', leavePolicy: '', team: '',    // Directus record IDs (strings)
};

const BLANK_FILES = { avatar: null, aadharFront: null, aadharBack: null };

// ── Icon helper ───────────────────────────────────────────────────────────────

function Icon({ name, style }) {
  return <span className="material-symbols-rounded" style={style}>{name}</span>;
}

// ── Camera modal ──────────────────────────────────────────────────────────────

/**
 * Full-screen (mobile) / centered-card (desktop) live camera viewfinder.
 *
 * All targets use the rear camera (environment) — a supervisor/manager is
 * the one taking the photo, never the worker doing a selfie.
 *
 * Guide overlays use CSS box-shadow cutout technique so the shape (oval for
 * avatar, 1.586 card ratio for Aadhar) is immune to container distortion —
 * aspect-ratio CSS keeps it correct on any screen size.
 */
function CameraModal({ target, onCapture, onClose }) {
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const isAvatar = target === 'avatar';

  useEffect(() => {
    let active = true;
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width:  { ideal: 1280 },
          height: { ideal: 720 },
        },
      })
      .then(stream => {
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => { if (active) setReady(true); };
        }
      })
      .catch(() => {
        if (active) setError('Camera access denied. Use the Gallery button to pick a photo instead.');
      });
    return () => {
      active = false;
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  function stopStream() {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  function capture() {
    const video = videoRef.current;
    if (!video || !ready) return;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      stopStream();
      const filename = isAvatar ? 'avatar.jpg' : `${target}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });
      onCapture(URL.createObjectURL(blob), file);
    }, 'image/jpeg', 0.9);
  }

  function handleClose() { stopStream(); onClose(); }

  const title = isAvatar ? 'Worker Photo'
    : target === 'aadharFront' ? 'Aadhar — Front' : 'Aadhar — Back';
  const hint = isAvatar ? 'Center face in the oval' : 'Align card within the frame';

  return (
    <div
      className="nwp-camera-overlay"
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`nwp-camera-inner ${isAvatar ? 'portrait' : 'landscape'}`}>

        <div className="nwp-camera-header">
          <span className="nwp-camera-title">{title}</span>
          <button className="nwp-camera-close-btn" onClick={handleClose} aria-label="Close camera">
            <Icon name="close" />
          </button>
        </div>

        {error ? (
          <div className="nwp-camera-error">
            <span className="material-symbols-rounded" style={{ fontSize: 44 }}>no_photography</span>
            <p>{error}</p>
          </div>
        ) : (
          <video
            ref={videoRef}
            className="nwp-camera-video"
            autoPlay
            playsInline
            muted
          />
        )}

        {/* CSS box-shadow cutout — aspect-ratio keeps shape correct on every screen */}
        {!error && (
          <div
            className={`nwp-camera-guide ${isAvatar ? 'face' : 'card'}`}
            aria-hidden="true"
          />
        )}

        <div className="nwp-camera-footer">
          {error ? (
            <button className="btn btn-secondary nwp-camera-btn" onClick={handleClose}>
              Close
            </button>
          ) : (
            <>
              <span className="nwp-camera-hint">{hint}</span>
              <button
                className="btn btn-primary nwp-camera-btn"
                onClick={capture}
                disabled={!ready}
              >
                <Icon name="photo_camera" />
                {ready ? 'Capture photo' : 'Starting camera…'}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Stepper ───────────────────────────────────────────────────────────────────

function Stepper({ step }) {
  return (
    <div className="nwp-stepper">
      {STEP_LABELS.map((label, i) => {
        const state = i < step ? 'done' : i === step ? 'active' : '';
        return (
          <React.Fragment key={label}>
            <div className={`nwp-step${state ? ` ${state}` : ''}`}>
              <div className="nwp-step-dot">
                {i < step ? <Icon name="check" /> : i + 1}
              </div>
              <span className="nwp-step-label">{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`nwp-step-line${i < step ? ' filled' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────

function NwpField({ label, required, optional, error, hint, counter, children }) {
  return (
    <div className={`nwp-field${error ? ' error' : ''}`}>
      <label>
        {label}
        {required  && <span className="nwp-req">*</span>}
        {optional  && <span className="nwp-optional">Optional</span>}
        {counter   && <span className="nwp-counter">{counter}</span>}
      </label>
      {children}
      {error
        ? <span className="nwp-field-msg"><Icon name="error" /> {error}</span>
        : hint
          ? <span className="nwp-field-hint">{hint}</span>
          : null
      }
    </div>
  );
}

// ── Step 0 — Personal ─────────────────────────────────────────────────────────

function Step0({ data, set, errors }) {
  return (
    <div className="nwp-screen">
      <div className="card">
        <p className="nwp-eyebrow">Worker details</p>

        <NwpField label="Full name" required error={errors.fullName}>
          <input
            className="nwp-input"
            type="text"
            placeholder="Worker's full name"
            value={data.fullName}
            autoComplete="off"
            autoFocus
            onChange={(e) => set({ fullName: e.target.value })}
          />
        </NwpField>

        <NwpField
          label="Phone"
          optional
          error={errors.phone}
          counter={data.phone ? `${data.phone.length}/10` : null}
        >
          <div className="nwp-phone-wrap">
            <span className="nwp-phone-prefix">+91</span>
            <input
              className="nwp-input"
              type="tel"
              inputMode="numeric"
              placeholder="10-digit number"
              value={data.phone}
              onChange={(e) => set({ phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
            />
          </div>
        </NwpField>

        <NwpField label="Date of joining" required error={errors.doj}>
          <input
            className="nwp-input"
            type="date"
            value={data.doj}
            onChange={(e) => set({ doj: e.target.value })}
          />
        </NwpField>

        <NwpField label="Date of birth" optional>
          <input
            className="nwp-input"
            type="date"
            value={data.dob}
            max={todays()}
            onChange={(e) => set({ dob: e.target.value })}
          />
        </NwpField>

        <NwpField
          label="Aadhar number"
          optional
          error={errors.aadhar}
          counter={data.aadhar ? `${data.aadhar.length}/12` : null}
          hint={!data.aadhar ? '12 digits — used for ID verification only' : null}
        >
          <input
            className="nwp-input"
            type="text"
            inputMode="numeric"
            placeholder="12-digit Aadhar number"
            value={groupAadhar(data.aadhar)}
            onChange={(e) => set({ aadhar: e.target.value.replace(/\D/g, '').slice(0, 12) })}
          />
        </NwpField>
      </div>
    </div>
  );
}

// ── Photo slot ────────────────────────────────────────────────────────────────

/**
 * Single photo capture slot.
 * "Camera" button → calls onCamera() which opens the getUserMedia modal.
 * "Gallery" button → opens a plain <input type="file"> (no capture attribute)
 *   so on desktop it opens the system file picker, on mobile it shows the
 *   gallery / files app.
 * onPick receives (blobUrl, File) so the parent stores both for display and upload.
 */
function PhotoSlot({ shape, label, hint, preview, onCamera, onPick, onClear }) {
  const galRef = useRef(null);

  const handleGallery = (e) => {
    const file = e.target.files?.[0];
    if (file) onPick(URL.createObjectURL(file), file);
    e.target.value = '';
  };

  const filled = !!preview;

  return (
    <div className="nwp-photo-block">
      <div className="nwp-photo-head">
        <span className="title">{label}</span>
      </div>
      <div className="nwp-photo-grid">
        <div className={`nwp-photo-slot ${shape}${filled ? ' filled' : ''}`}>
          {filled ? (
            <>
              <img src={preview} alt={label} />
              <span className="filled-badge"><Icon name="check" /></span>
            </>
          ) : (
            <>
              <Icon name="add_a_photo" />
              <span className="slot-hint">{hint}</span>
            </>
          )}
        </div>

        <div className="nwp-photo-controls">
          {filled ? (
            <>
              <button type="button" className="nwp-chip-btn" onClick={onCamera}>
                <Icon name="refresh" /> Retake
              </button>
              <button type="button" className="nwp-chip-btn danger" onClick={onClear}>
                <Icon name="delete" /> Remove
              </button>
            </>
          ) : (
            <>
              <button type="button" className="nwp-chip-btn" onClick={onCamera}>
                <Icon name="photo_camera" /> Camera
              </button>
              <button type="button" className="nwp-chip-btn" onClick={() => galRef.current.click()}>
                <Icon name="photo_library" /> Gallery
              </button>
            </>
          )}
        </div>
      </div>

      {/* No `capture` attribute — lets the OS decide (file picker on desktop, gallery on mobile) */}
      <input ref={galRef} type="file" accept="image/*" hidden onChange={handleGallery} />
    </div>
  );
}

// ── Step 1 — Photos ───────────────────────────────────────────────────────────

function Step1({ data, set, setFile, onCameraOpen }) {
  return (
    <div className="nwp-screen">
      <div className="card">
        <p className="nwp-eyebrow">Photos &amp; ID</p>

        <PhotoSlot
          shape="avatar"    label="Worker avatar"   hint="Front-facing photo"
          preview={data.avatar}
          onCamera={() => onCameraOpen('avatar')}
          onPick={(url, file) => { set({ avatar: url });       setFile('avatar',      file); }}
          onClear={() =>          { set({ avatar: null });      setFile('avatar',      null); }}
        />
        <PhotoSlot
          shape="card-shot" label="Aadhar — front"  hint="Front side of card"
          preview={data.aadharFront}
          onCamera={() => onCameraOpen('aadharFront')}
          onPick={(url, file) => { set({ aadharFront: url });  setFile('aadharFront', file); }}
          onClear={() =>          { set({ aadharFront: null }); setFile('aadharFront', null); }}
        />
        <PhotoSlot
          shape="card-shot" label="Aadhar — back"   hint="Back side of card"
          preview={data.aadharBack}
          onCamera={() => onCameraOpen('aadharBack')}
          onPick={(url, file) => { set({ aadharBack: url });   setFile('aadharBack',  file); }}
          onClear={() =>          { set({ aadharBack: null });  setFile('aadharBack',  null); }}
        />

        <div className="nwp-photo-tip">
          <Icon name="info" />
          <span>All photos are optional — you can add them now or from the worker's profile later.</span>
        </div>
      </div>
    </div>
  );
}

// ── Step 2 — Employment ───────────────────────────────────────────────────────

function Step2({ data, set, shifts, rates, leavePolicies, teams }) {
  return (
    <div className="nwp-screen">
      <div className="card">
        <p className="nwp-eyebrow">Assignment</p>

        <NwpField label="Team" required>
          <select className="nwp-select" value={data.team}
            onChange={(e) => set({ team: e.target.value })}>
            <option value="">Select team</option>
            {teams.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </select>
        </NwpField>

        <NwpField label="Shift" required>
          <select className="nwp-select" value={data.shift}
            onChange={(e) => set({ shift: e.target.value })}>
            <option value="">Select shift</option>
            {shifts.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>
        </NwpField>

        <NwpField label="Rate" required>
          <select className="nwp-select" value={data.rate}
            onChange={(e) => set({ rate: e.target.value })}>
            <option value="">Select rate</option>
            {rates.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
          </select>
        </NwpField>

        <NwpField label="Leave policy" required>
          <select className="nwp-select" value={data.leavePolicy}
            onChange={(e) => set({ leavePolicy: e.target.value })}>
            <option value="">Select policy</option>
            {leavePolicies.map(lp => <option key={lp.id} value={String(lp.id)}>{lp.name}</option>)}
          </select>
        </NwpField>
      </div>
    </div>
  );
}

// ── Review bottom sheet ───────────────────────────────────────────────────────

function ReviewRow({ k, v }) {
  return (
    <div className="nwp-review-row">
      <span className="k">{k}</span>
      <span className={`v${v ? '' : ' empty'}`}>{v || 'Not added'}</span>
    </div>
  );
}

function ReviewSheet({ data, dropdowns, onClose, onConfirm, saving }) {
  const { shifts, rates, leavePolicies, teams } = dropdowns;

  const nameById = (list, id) =>
    list.find(x => String(x.id) === String(id))?.name || '';

  const photos = [
    data.avatar      && { src: data.avatar,      circle: true  },
    data.aadharFront && { src: data.aadharFront, circle: false },
    data.aadharBack  && { src: data.aadharBack,  circle: false },
  ].filter(Boolean);

  return (
    <div className="nwp-sheet-overlay" onClick={onClose}>
      <div className="nwp-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="nwp-sheet-grab" />
        <div className="nwp-sheet-head">
          <h2>Review &amp; confirm</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 36, height: 36, border: 0, background: 'transparent',
              cursor: 'pointer', borderRadius: 999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--fg)',
            }}
          >
            <Icon name="close" style={{ fontSize: 22 }} />
          </button>
        </div>

        <div className="nwp-sheet-body">
          <div className="nwp-review-group">
            <p className="nwp-review-group-title">Personal</p>
            <ReviewRow k="Full name"       v={data.fullName} />
            <ReviewRow k="Phone"           v={data.phone ? `+91 ${data.phone}` : ''} />
            <ReviewRow k="Date of joining" v={fmtDate(data.doj)} />
            <ReviewRow k="Date of birth"   v={fmtDate(data.dob)} />
            <ReviewRow k="Aadhar number"   v={data.aadhar ? groupAadhar(data.aadhar) : ''} />
          </div>

          <div className="nwp-review-group">
            <p className="nwp-review-group-title">Photos</p>
            {photos.length ? (
              <div className="nwp-review-photos">
                {photos.map((p, i) => (
                  <img key={i} className={`thumb${p.circle ? ' avatar' : ''}`} src={p.src} alt="" />
                ))}
              </div>
            ) : (
              <ReviewRow k="Photos" v="" />
            )}
          </div>

          <div className="nwp-review-group">
            <p className="nwp-review-group-title">Employment</p>
            <ReviewRow k="Team"         v={nameById(teams,         data.team)} />
            <ReviewRow k="Shift"        v={nameById(shifts,        data.shift)} />
            <ReviewRow k="Rate"         v={nameById(rates,         data.rate)} />
            <ReviewRow k="Leave policy" v={nameById(leavePolicies, data.leavePolicy)} />
          </div>
        </div>

        <div className="nwp-sheet-foot">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            <Icon name="edit" /> Edit
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={onConfirm}
            disabled={saving}
          >
            {saving
              ? <><Icon name="hourglass_empty" /> Saving…</>
              : <><Icon name="person_add" /> Add worker</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Success screen ────────────────────────────────────────────────────────────

function Success({ name, avatar, meta, onAnother, onDone }) {
  const initials = (name || '')
    .trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="nwp-success">
      <div className="nwp-success-avatar">
        {avatar
          ? <img src={avatar} alt={name} />
          : <span className="fallback">{initials || <Icon name="person" />}</span>
        }
        <span className="nwp-success-check"><Icon name="check" /></span>
      </div>

      <h2>{name}</h2>
      {meta && <p className="nwp-success-meta">{meta}</p>}
      <p>Added to your team and ready for attendance.</p>

      <div className="nwp-success-actions">
        <button type="button" className="btn btn-primary btn-block" onClick={onDone}>
          <Icon name="space_dashboard" /> Go to dashboard
        </button>
        <button type="button" className="btn btn-tertiary" onClick={onAnother}>
          <Icon name="add" /> Add another worker
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function NewWorkerPage() {
  const navigate       = useNavigate();
  const { isManager }  = useAuth();

  const [step,         setStep]        = useState(0);
  const [data,         setData]        = useState(BLANK);
  const [files,        setFilesState]  = useState(BLANK_FILES);
  const [errors,       setErrors]      = useState({});
  const [cameraTarget, setCameraTarget] = useState(null); // 'avatar' | 'aadharFront' | 'aadharBack'
  const [showReview,   setShowReview]  = useState(false);
  const [done,         setDone]        = useState(false);
  const [saving,       setSaving]      = useState(false);

  // Dropdown options loaded from Directus
  const [shifts,        setShifts]        = useState([]);
  const [rates,         setRates]         = useState([]);
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [teams,         setTeams]         = useState([]);

  const bodyRef = useRef(null);

  // Merge-patch helper — also clears per-field errors so the UI reacts immediately
  const set = useCallback((patch) => {
    setData(d => ({ ...d, ...patch }));
    setErrors(er => {
      const next = { ...er };
      Object.keys(patch).forEach(k => delete next[k]);
      return next;
    });
  }, []);

  const setFile = (key, file) => setFilesState(f => ({ ...f, [key]: file }));

  // Fetch all relationship dropdowns in parallel; pre-select first item each
  useEffect(() => {
    Promise.all([getShifts(), getRates(), getLeavePolicies(), getTeams()])
      .then(([s, r, lp, t]) => {
        const sd = s.data || [], rd = r.data || [], lpd = lp.data || [], td = t.data || [];
        setShifts(sd);
        setRates(rd);
        setLeavePolicies(lpd);
        setTeams(td);
        setData(d => ({
          ...d,
          shift:       sd[0]  ? String(sd[0].id)  : d.shift,
          rate:        rd[0]  ? String(rd[0].id)  : d.rate,
          leavePolicy: lpd[0] ? String(lpd[0].id) : d.leavePolicy,
          team:        td[0]  ? String(td[0].id)  : d.team,
        }));
      });
  }, []);

  // Scroll back to top whenever the step changes
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step]);

  // Push a dummy history entry when the camera opens so the browser back
  // gesture / Android back button closes the modal instead of leaving the page.
  useEffect(() => {
    if (!cameraTarget) return;
    window.history.pushState({ cameraModal: true }, '');
    const onPop = () => setCameraTarget(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [cameraTarget]);

  // Manager-only guard — placed after hooks to keep hook call order stable
  if (!isManager) return <Navigate to="/" replace />;

  // ── Camera ──────────────────────────────────────────────────────────────────

  /** Pop the history entry we pushed on open, then close the modal. */
  function closeCameraModal() {
    window.history.back();
    setCameraTarget(null);
  }

  function handleCapture(url, file) {
    set({ [cameraTarget]: url });
    setFile(cameraTarget, file);
    closeCameraModal();
  }

  // ── Validation ──────────────────────────────────────────────────────────────

  function validateStep0() {
    const e = {};
    if (!data.fullName.trim()) e.fullName = "Enter the worker's name";
    if (!data.doj)             e.doj      = 'Pick a joining date';
    if (data.phone  && data.phone.length  !== 10) e.phone  = 'Phone must be 10 digits';
    if (data.aadhar && data.aadhar.length !== 12) e.aadhar = 'Aadhar must be 12 digits';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ── Navigation ──────────────────────────────────────────────────────────────

  function handleNext() {
    if (step === 0 && !validateStep0()) return;
    if (step === 2) { setShowReview(true); return; }
    setStep(s => Math.min(2, s + 1));
  }

  function handleBack() {
    if (step === 0) { navigate('/'); return; }
    setStep(s => s - 1);
  }

  // Topbar back arrow: go to previous step, or home from step 0
  function handleTopBack() {
    if (step > 0) setStep(s => s - 1);
    else navigate('/');
  }

  function handleReset() {
    setData(BLANK);
    setFilesState(BLANK_FILES);
    setErrors({});
    setStep(0);
    setShowReview(false);
    setDone(false);
  }

  // ── Submit — upload photos in parallel, then create worker ──────────────────

  async function handleConfirm() {
    setSaving(true);
    try {
      const [photoId, frontId, backId] = await Promise.all([
        files.avatar      ? uploadTo(files.avatar,      PHOTO_FOLDER)  : Promise.resolve(null),
        files.aadharFront ? uploadTo(files.aadharFront, AADHAR_FOLDER) : Promise.resolve(null),
        files.aadharBack  ? uploadTo(files.aadharBack,  AADHAR_FOLDER) : Promise.resolve(null),
      ]);

      await createWorker({
        name:         data.fullName.trim(),
        phone:        data.phone ? `+91${data.phone}` : undefined,
        doj:          data.doj,
        dob:          data.dob       || undefined,
        aadhar_no:    data.aadhar    || undefined,
        photo:        photoId        || undefined,
        aadhar_front: frontId        || undefined,
        aadhar_back:  backId         || undefined,
        shift:        parseInt(data.shift,       10),
        rate:         parseInt(data.rate,        10),
        leave_policy: parseInt(data.leavePolicy, 10),
        team:         parseInt(data.team,        10),
        status:       'active',
      });

      playSuccessSound();
      setShowReview(false);
      setDone(true);
    } catch (err) {
      playErrorSound();
      showAlert(err?.errors?.[0]?.message || 'Failed to add worker — please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  /** Build FormData and upload to a Directus folder; returns the file UUID. */
  async function uploadTo(file, folderId) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await uploadFile(fd, folderId);
    return res.data?.id ?? null;
  }

  // Team name for the success screen meta line
  const teamName = teams.find(t => String(t.id) === String(data.team))?.name || '';

  // ── Success screen ──────────────────────────────────────────────────────────

  if (done) {
    return (
      <div className="nwp-page">
        <div className="topbar">
          <button className="icon-btn" onClick={handleReset} aria-label="Add another worker">
            <span className="material-symbols-rounded">arrow_back</span>
          </button>
          <h1>New worker</h1>
        </div>
        <Success
          name={data.fullName || 'New worker'}
          avatar={data.avatar}
          meta={`${teamName}${teamName ? ' · ' : ''}Joined ${fmtDate(data.doj)}`}
          onAnother={handleReset}
          onDone={() => navigate('/')}
        />
      </div>
    );
  }

  // ── Wizard ──────────────────────────────────────────────────────────────────

  return (
    <div className="nwp-page">
      <div className="topbar">
        <button className="icon-btn" onClick={handleTopBack} aria-label="Back">
          <span className="material-symbols-rounded">arrow_back</span>
        </button>
        <h1>New worker</h1>
      </div>

      <Stepper step={step} />

      <div className="nwp-body" ref={bodyRef}>
        {step === 0 && <Step0 data={data} set={set} errors={errors} />}
        {step === 1 && (
          <Step1
            data={data} set={set} setFile={setFile}
            onCameraOpen={setCameraTarget}
          />
        )}
        {step === 2 && (
          <Step2
            data={data} set={set}
            shifts={shifts} rates={rates}
            leavePolicies={leavePolicies} teams={teams}
          />
        )}
      </div>

      <div className="nwp-footer">
        {step === 0
          ? <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>Cancel</button>
          : (
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              <Icon name="arrow_back" /> Back
            </button>
          )
        }
        <div className="spacer" />
        <button type="button" className="btn btn-primary" onClick={handleNext}>
          {step === 2
            ? <><span>Review &amp; add</span> <Icon name="arrow_forward" /></>
            : <><span>Next</span> <Icon name="arrow_forward" /></>
          }
        </button>
      </div>

      {/* Camera modal — portalled outside the scrollable body */}
      {cameraTarget && (
        <CameraModal
          target={cameraTarget}
          onCapture={handleCapture}
          onClose={closeCameraModal}
        />
      )}

      {showReview && (
        <ReviewSheet
          data={data}
          dropdowns={{ shifts, rates, leavePolicies, teams }}
          onClose={() => setShowReview(false)}
          onConfirm={handleConfirm}
          saving={saving}
        />
      )}
    </div>
  );
}
